// src/oura/delivery/WorkParts.tsx
//
// Phase 3 inside an opened item (contract v1.3, H32 to H39; scope 3.1 to
// 3.9). There is no job or change table: the deliverable is the job and its
// items are the work, so each item shows here what people did on it.
//
//   Drafts        the drafts a person started for this item, in the Pages
//                 workshop or Site issues, and where each stands. A published
//                 fix draft counts as Mark fixed, so Oura's re-check closes it.
//   Paste list    a change Oura cannot put live on this site: the click path
//                 and the exact words per step, a tick per step (the last
//                 tick marks it fixed) and a plain-text list for a developer.
//   Live since    the reply for the doc's approval thread once the change is
//                 live, drafted for a person to post: "Post it in the doc" is
//                 that person's click, signed with their name (B20), and the
//                 item records who posted it. Never posted by Oura.
//   Approved copy an unpublished draft page where the connection allows (a
//                 WordPress page), else the copy as a file to paste in: Oura
//                 cannot create a page or a post on Wix or Framer.
//   AI readers    one line per reader, "Not tested" until the first test;
//                 the test closes A3's readers item when all 3 get in.
//   Proof file    the file a person attached when closing it by hand.
//
// Renders nothing without DELIVERY_WORK_ON (Delivery and the Docs writes),
// so a build missing either switch shows none of it. Writes go through the
// drawer's own `onWrite`, so its messages and errors stay in one place.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { CheckIcon, ClipboardCopyIcon, DownloadIcon, ExternalLinkIcon, FileTextIcon } from "lucide-react";
import type { Deliverable, DeliveryFailure, DeliveryItem, ReaderCheck, SitePlatform, StartedDraft } from "@service/aeo/delivery";
import {
    UNTESTED_READERS,
    addLivePage,
    copyToSite,
    isReadersItem,
    liveSinceMayHavePostedText,
    liveSincePostedHere,
    postLiveSince,
    shortDay,
    testAiReaders,
    tickPasteStep,
} from "@service/aeo/delivery";
import { getDocument } from "@service/aeo/clientDocs";
import { docNameOf, myDate } from "@service/aeo/clientDocsRules";
import { cn } from "../lib/cn";
import { Dot, FOCUS_RING, ListRow, PillButton, SURFACE, TYPE, type Tone } from "../ui";
import { plural } from "./format";
import { DELIVERY_WORK_ON } from "./workFlag";
import { readerPasses } from "./workModel";
import { useRecordedYes } from "../components/docs/threads/model";
import { samplePageCopyKey } from "@service/aeo/publishRefusal";

/** A write and what the drawer says once it lands; a `note` on the result, when there is one, is said instead. */
export type WorkWrite = (said: string, write: () => Promise<{ ok: true; data: Deliverable; note?: string } | DeliveryFailure>) => Promise<boolean>;

const PLATFORM: Record<SitePlatform, string> = {
    wix: "Wix",
    wordpress: "WordPress",
    framer: "Framer",
    webflow: "Webflow",
    squarespace: "Squarespace",
    other: "a custom-built site",
};

const READER_NAME: Record<ReaderCheck["reader"], string> = { chatgpt: "ChatGPT", claude: "Claude", perplexity: "Perplexity" };

/** "2 Oct": a day from an instant or a date. */
const day = (iso: string | null | undefined) => (iso ? shortDay(myDate(iso)) : "");

/**
 * A file Oura hands over, named the way a client doc is (#5254):
 * `(<code>) <Client>_<Topic>[_<Location>]_<DD MMMM YYYY>.<ext>` (no version
 * since #4874), so the
 * paste list and the backend's approved-copy download for the SAME slot read
 * as one pair. The code and the place are left out when the slot has none.
 *
 * `docDateOf` and `docNameOf` are the shared builders, so this file and every
 * doc name move together. The characters a file system refuses are still
 * cleaned off the topic; the date is Malaysian, as `myDate` gives it.
 */
function fileName(client: string, topic: string, code: string | null, outlet: string | null, ext = "txt"): string {
    const name = docNameOf({
        client,
        docType: topic.replace(/[\\/:*?"<>|]+/g, " ").trim(),
        codes: code ? [code] : [],
        location: outlet,
        date: myDate(new Date()),
    });
    return `${name}.${ext}`;
}

function downloadText(text: string, name: string): void {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

/** A small Copy control that says Copied for a moment. */
function CopyButton({ text, label = "Copy", size = "sm", variant = "secondary" }: { text: string; label?: string; size?: "sm" | "md"; variant?: "primary" | "secondary" }) {
    const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
    return (
        <PillButton
            size={size}
            variant={variant}
            icon={state === "copied" ? CheckIcon : ClipboardCopyIcon}
            onClick={() =>
                void copyText(text).then((ok) => {
                    setState(ok ? "copied" : "failed");
                    window.setTimeout(() => setState("idle"), 2000);
                })
            }
        >
            {state === "copied" ? "Copied" : state === "failed" ? "Select and copy" : label}
        </PillButton>
    );
}

/** A labelled grey block inside an opened item. */
function WorkBlock({ label, fact, children }: { label: string; fact?: ReactNode; children: ReactNode }) {
    return (
        <div className={cn(SURFACE.inner, "space-y-2")}>
            <p className={cn(TYPE.lab, "m-0 flex flex-wrap items-baseline gap-x-2")}>
                <span>{label}</span>
                {fact && <span className="font-normal text-gray-500">{fact}</span>}
            </p>
            {children}
        </div>
    );
}

const TEXT_LINK = cn(TYPE.tc, "shrink-0 rounded font-medium text-blue-700 hover:underline", FOCUS_RING);

/* ------------------------------------------------------------------------- *
 * Drafts a person started (H32, H33, H34)
 * ------------------------------------------------------------------------- */

const DRAFT_TONE: Record<StartedDraft["state"], Tone> = { draft: "blue", published: "green", pasted: "green" };

function DraftRow({ draft, item, onOpenDoc, onGoTo }: { draft: StartedDraft; item: DeliveryItem; onOpenDoc: (docId: string) => void; onGoTo: (view: string, search?: string) => void }) {
    const fix = draft.kind === "fix";
    const open = () =>
        fix
            ? onGoTo("setup", item.checkKey ? `?issue=${encodeURIComponent(item.checkKey)}` : undefined)
            : onGoTo("pages", `?page=${encodeURIComponent(draft.page)}&title=${encodeURIComponent(draft.label)}`);
    const said =
        draft.state === "published"
            ? `Published ${day(draft.publishedAt)}${draft.publishedBy ? ` by ${draft.publishedBy}` : ""}${fix ? " · counts as Mark fixed" : ""}`
            : draft.state === "pasted"
              ? "Pasted by hand from the paste list"
              : `Draft for ${draft.page}, started by ${draft.startedBy} ${day(draft.startedAt)} · ${
                    fix ? "publish it in Site issues" : item.copyToSite && !item.copyToSite.allowed ? `paste it on ${PLATFORM[item.copyToSite.platform ?? "other"]} from the copy below` : "publish it in Pages"
                }`;
    return (
        <li className={cn(TYPE.tc, "flex items-start gap-2 py-1")}>
            <Dot tone={DRAFT_TONE[draft.state]} className="mt-[5px]" />
            <span className="min-w-0 flex-1">
                <span className="block break-words text-gray-900">{draft.label}</span>
                <span className="block text-gray-500">{said}</span>
                {draft.fromDocId && (
                    <button type="button" onClick={() => onOpenDoc(draft.fromDocId!)} className={cn(TEXT_LINK, "mt-0.5 block text-left")}>
                        {draft.fromQuestionNumber ? `From the client's answer to Q${draft.fromQuestionNumber}` : draft.fromQuestionId ? "From the client's answer" : "From the approved doc"}
                    </button>
                )}
            </span>
            <button type="button" onClick={open} className={TEXT_LINK}>
                {fix ? "Open in Site issues" : "Open in Pages"}
            </button>
        </li>
    );
}

/* ------------------------------------------------------------------------- *
 * Paste list (H35)
 * ------------------------------------------------------------------------- */

function PasteListBlock({ item, code, businessId, businessName, busy, onWrite }: { item: DeliveryItem; code: string | null; businessId: string; businessName: string; busy: boolean; onWrite: WorkWrite }) {
    const list = item.pasteList!;
    const ticked = list.steps.filter((s) => s.ticked).length;
    // Driven by the ticks as they stand, never by a flag an undo left behind.
    const allTicked = list.steps.length > 0 && ticked === list.steps.length;
    // A page's copy (not a site check): once every block is in, a person marks it live and the next scan confirms it.
    const pageCopy = item.kind !== "check";
    const livePage = item.pages[0]?.url ?? null;
    const markedLive = item.pages.some((p) => p.state !== "todo" && p.markedFixedAt) || item.state === "waiting" || item.state === "done";
    const custom = list.platform === "other";
    const exportList = () => downloadText(list.forDeveloper, fileName(businessName, `Developer List ${item.label}`, code, item.outlet ?? null));
    return (
        <WorkBlock label={`Paste list on ${PLATFORM[list.platform]}`} fact={`${ticked} of ${plural(list.steps.length, pageCopy ? "block" : "step")} pasted`}>
            {custom && <p className={cn(TYPE.tc, "m-0 text-gray-600")}>Oura cannot change this site. Send the list to the client&rsquo;s developer, then tick each step they confirm.</p>}
            <ol className="m-0 list-plain space-y-2 p-0">
                {list.steps.map((s, i) => (
                    <li key={s.id} className="flex items-start gap-2.5">
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={s.ticked}
                            aria-label={`Step ${i + 1}: ${s.where}`}
                            disabled={busy}
                            onClick={() => {
                                const last = !s.ticked && ticked === list.steps.length - 1;
                                const said = s.ticked
                                    ? `Step ${i + 1} unticked`
                                    : last
                                      ? pageCopy
                                          ? `All ${list.steps.length} blocks pasted. Mark it live when the page shows them`
                                          : `All ${list.steps.length} steps pasted. ${item.label} marked fixed`
                                      : `Step ${i + 1} ticked`;
                                void onWrite(said, () => tickPasteStep(businessId, item.id, s.id, !s.ticked));
                            }}
                            className={cn(
                                "mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-2 disabled:opacity-40",
                                s.ticked ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300 bg-white",
                                FOCUS_RING,
                            )}
                        >
                            {s.ticked && <CheckIcon aria-hidden size={10} strokeWidth={3} />}
                        </button>
                        <span className="min-w-0 flex-1 space-y-1">
                            <span className={cn(TYPE.tc, "block break-words text-gray-900")}>{s.where}</span>
                            {s.words && (
                                <span className="flex flex-wrap items-start gap-2">
                                    <span className={cn(TYPE.tc, "min-w-0 flex-1 basis-40 break-words rounded-lg bg-white px-2.5 py-1.5 text-gray-900 ring-1 ring-inset ring-black/[0.06]")}>{s.words}</span>
                                    <CopyButton text={s.words} />
                                </span>
                            )}
                            {s.ticked && s.tickedBy && <span className={cn(TYPE.tm, "block text-gray-500")}>Ticked by {s.tickedBy} {day(s.tickedAt)}</span>}
                        </span>
                    </li>
                ))}
            </ol>
            {pageCopy && allTicked && !markedLive && livePage && (
                <div className="flex flex-wrap items-center gap-2">
                    <PillButton
                        size="sm"
                        variant="primary"
                        busy={busy}
                        onClick={() =>
                            void onWrite("Marked live. Oura confirms it on tonight's scan", () =>
                                addLivePage(businessId, item.id, {
                                    url: livePage,
                                    pageKind: item.slot?.pageKind === "existing" ? "existing" : "new",
                                    changed: item.slot?.pageKind === "existing" ? "The approved copy, pasted in" : undefined,
                                }),
                            )
                        }
                    >
                        Mark live
                    </PillButton>
                    <span className={cn(TYPE.tm, "text-gray-500")}>Oura confirms it on tonight&rsquo;s scan.</span>
                </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span className={cn(TYPE.tm, "text-gray-500")}>
                    {pageCopy
                        ? markedLive
                            ? "Marked live. Oura confirms it on tonight's scan."
                            : "Tick each block once it is on the page, then mark it live."
                        : allTicked
                          ? "All pasted: marked fixed. Oura re-checks it tonight."
                          : "The last tick marks it fixed. Oura re-checks it tonight."}
                </span>
                <button type="button" onClick={exportList} className={cn(TEXT_LINK, "inline-flex items-center gap-1")}>
                    <DownloadIcon aria-hidden size={12} />A list for the developer
                </button>
            </div>
        </WorkBlock>
    );
}

/* ------------------------------------------------------------------------- *
 * "Live since" reply (H37)
 * ------------------------------------------------------------------------- */

function LiveSinceBlock({ item, deliverable, businessId, busy, onWrite, onOpenDoc }: {
    item: DeliveryItem;
    deliverable: Deliverable;
    businessId: string;
    busy: boolean;
    onWrite: WorkWrite;
    onOpenDoc: (docId: string) => void;
}) {
    const draft = item.liveSinceDraft!;
    // Posted from this tab reads as posted even before the server says so: the reply is in the client's doc, and a second click would post it twice.
    const r = draft.postedAt ? draft : { ...draft, postedAt: liveSincePostedHere(businessId, item.id, draft) };
    // The last post lost its answer: it may be in the doc. Never offered as a plain Post again;
    // the person checks the thread first, and only "Post it anyway" sends it.
    // The words it tried with are the only words a retry may send, so they show read-only until the doubt ends.
    const doubtfulText = liveSinceMayHavePostedText(businessId, item.id, draft);
    const doubt = doubtfulText !== null && !r.postedAt;
    // The words are read and can be changed before they post; the post waits a few seconds so it can be undone.
    const [text, setText] = useState(r.text);
    const [pending, setPending] = useState<number | null>(null);
    const [anyway, setAnyway] = useState(false);
    const [docUrl, setDocUrl] = useState<string | null>(null);
    const displayedText = doubtfulText ?? text;
    const timer = useRef<number | null>(null);
    useEffect(() => () => {
        if (timer.current !== null) window.clearTimeout(timer.current);
    }, []);
    // In doubt, the Google Doc itself is one click away, to check the thread. Every
    // change of draft drops the last link first, so it never points at another draft's doc.
    useEffect(() => {
        setDocUrl(null);
        if (!doubt) return;
        let live = true;
        void getDocument(draft.docId)
            .then((d) => {
                if (!live || !d.ok) return;
                const url = googleDocsUrl(d.data.url);
                if (url) setDocUrl(url);
            })
            .catch(() => {
                // The link stays hidden; "Open the doc" is still there.
            });
        return () => {
            live = false;
        };
    }, [doubt, draft.docId]);
    const post = (postAnyway = false) => {
        const words = displayedText.trim();
        if (!words || pending !== null) return;
        setPending(UNDO_SECONDS);
        setAnyway(postAnyway);
        const started = Date.now();
        const tick = () => {
            const left = UNDO_SECONDS - Math.floor((Date.now() - started) / 1000);
            if (left > 0) {
                setPending(left);
                timer.current = window.setTimeout(tick, 250);
                return;
            }
            timer.current = null;
            setPending(null);
            void onWrite("Live since reply posted in the doc", () => postLiveSince(businessId, { ...item, liveSinceDraft: { ...r, text: words } }, deliverable, { anyway: postAnyway }));
        };
        timer.current = window.setTimeout(tick, 250);
    };
    const undo = () => {
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = null;
        setPending(null);
    };
    return (
        <WorkBlock label="Live since reply" fact={r.postedAt ? `posted ${day(r.postedAt)}${r.postedBy ? ` by ${r.postedBy}` : ""}` : "drafted, not posted"}>
            {r.postedAt ? (
                <p className={cn(TYPE.tc, "m-0 whitespace-pre-wrap break-words rounded-lg bg-white px-2.5 py-1.5 text-gray-900 ring-1 ring-inset ring-black/[0.06]")}>{r.text}</p>
            ) : (
                <textarea
                    value={displayedText}
                    rows={3}
                    disabled={pending !== null || busy}
                    readOnly={doubt}
                    onChange={(e) => {
                        if (!doubt) setText(e.target.value);
                    }}
                    aria-label={doubt ? "The Live since reply, as it was sent" : "The Live since reply, as it will post"}
                    className={cn(TYPE.tc, "block w-full resize-y rounded-lg border-0 bg-white px-2.5 py-1.5 text-gray-900 ring-1 ring-inset ring-black/[0.06] focus:outline-none focus:ring-2 focus:ring-blue-600")}
                />
            )}
            {/* Both sides kept: #3981's `!doubt` guard (a doubtful reply is not
                offered again as if it had never posted) with staging's wording
                from the 7 Oct usability walk, "as Oura" — the address is not
                the same on every environment, so no screen names it. */}
            {!r.postedAt && !doubt && <p className={cn(TYPE.tm, "m-0 text-gray-500")}>It posts in the doc&rsquo;s approval thread as Oura, signed with your name.</p>}
            {doubt && (
                <p role="alert" className={cn(TYPE.tc, "m-0 flex flex-wrap items-baseline gap-x-2 font-medium text-amber-800")}>
                    <span className="min-w-0">The reply may have posted. Open the doc and check the thread before posting again.</span>
                    {docUrl && (
                        <a href={docUrl} target="_blank" rel="noopener noreferrer" className={cn(TEXT_LINK, "inline-flex items-center gap-1")}>
                            Open in Google Docs
                            <ExternalLinkIcon aria-hidden size={12} />
                            <span className="sr-only">, opens in a new tab</span>
                        </a>
                    )}
                </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
                {!r.postedAt && !doubt && pending === null && (
                    <PillButton size="sm" variant="primary" busy={busy} disabled={!displayedText.trim()} onClick={() => post()}>
                        Post it in the doc
                    </PillButton>
                )}
                {!r.postedAt && pending !== null && (
                    <>
                        <span role="status" className={cn(TYPE.tc, "text-gray-700")}>
                            {anyway ? "Posting again" : "Posting"} in {pending}s
                        </span>
                        <PillButton size="sm" variant="primary" onClick={undo}>
                            Undo
                        </PillButton>
                    </>
                )}
                {!r.postedAt && <CopyButton text={displayedText} label="Copy the reply" />}
                <PillButton size="sm" icon={FileTextIcon} onClick={() => onOpenDoc(r.docId)}>
                    Open the doc
                </PillButton>
                {doubt && pending === null && (
                    <PillButton size="sm" variant="text" busy={busy} disabled={!displayedText.trim()} onClick={() => post(true)}>
                        Post it anyway
                    </PillButton>
                )}
            </div>
        </WorkBlock>
    );
}

/** How long a Live since post waits, so it can be taken back. */
const UNDO_SECONDS = 5;

/** A doc's link, only when it is a Google Doc: "Open in Google Docs" never leads anywhere else. */
function googleDocsUrl(raw: string): string | null {
    try {
        const url = new URL(raw);
        return url.protocol === "https:" && url.hostname === "docs.google.com" ? url.toString() : null;
    } catch {
        return null;
    }
}

/* ------------------------------------------------------------------------- *
 * Approved copy to the site (H39)
 * ------------------------------------------------------------------------- */

/** The H20 keys an item's client yes is recorded on: a page slot's own id ("G1-m2:slot-1") or code plus check ("S1:missing_meta"), and its drafts' keys. */
function approvalKeysOf(item: DeliveryItem, code: string): string[] {
    const own = item.kind === "check" && item.checkKey ? `${code}:${item.checkKey}` : item.id;
    return Array.from(new Set([own, ...(item.drafts ?? []).map((d) => d.approvalKey).filter((k): k is string => Boolean(k))]));
}

function CopyToSiteBlock({ item, deliverable, businessId, businessName, busy, onWrite, onOpenDoc }: {
    item: DeliveryItem;
    deliverable: Pick<Deliverable, "code" | "document">;
    businessId: string;
    businessName: string;
    busy: boolean;
    onWrite: WorkWrite;
    onOpenDoc: (docId: string) => void;
}) {
    const c = item.copyToSite!;
    // One approval source with the Pages workshop: a yes recorded on this item's own key, and on the sample's
    // page copy key only on the in-browser sample, where the workshop records it.
    const sampleKey = samplePageCopyKey(businessId);
    const keys = [...approvalKeysOf(item, deliverable.code), ...(sampleKey ? [sampleKey] : [])];
    const recorded = useRecordedYes(businessId, keys.map((k) => k.split(":")[0])).some((o) => keys.includes(o.taskId));
    // H39: CopyToSite carries no approved flag yet, so the doc it names reading approved (H20's first source) counts too.
    const doc = deliverable.document;
    const clientYes = recorded || Boolean(doc?.stateKey === "approved" && (!c.docId || c.docId === doc.docId));
    const canDraft = c.mode === "draft_page" && c.allowed && !c.draftUrl;
    // Where Oura cannot make a draft page, the copy comes as a file: "Get the copy" until it is ready.
    const canGet = !canDraft && !c.draftUrl && !c.downloadUrl;
    const download = c.downloadUrl ? (
        <a
            href={c.downloadUrl}
            download={fileName(businessName, `${item.label} Copy`, deliverable.code, item.outlet ?? null)}
            target={c.downloadUrl.startsWith("data:") ? undefined : "_blank"}
            rel="noopener noreferrer"
            className={cn(TEXT_LINK, "inline-flex items-center gap-1")}
        >
            <DownloadIcon aria-hidden size={12} />
            Download
        </a>
    ) : null;
    return (
        <WorkBlock
            label={clientYes ? "Approved copy to the site" : "Copy to the site"}
            fact={c.draftUrl ? `draft page on ${PLATFORM[c.platform ?? "other"]}, not published` : clientYes ? "the client said yes" : "waits on the client's yes"}
        >
            {!clientYes && <p className={cn(TYPE.tc, "m-0 font-medium text-amber-800")}>The client has not said yes to this wording yet. It needs their yes in the doc.</p>}
            <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                {c.draftUrl
                    ? "A named person publishes it from the site."
                    : canDraft
                      ? `Oura can put it on ${PLATFORM[c.platform ?? "other"]} as a draft page. It stays unpublished until a named person publishes it.`
                      : c.reason ?? `Oura cannot create pages on ${PLATFORM[c.platform ?? "other"]}. Get the copy and paste it in.`}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {canDraft && (
                    <PillButton size="sm" variant="primary" busy={busy} onClick={() => void onWrite("Draft page made, not published", () => copyToSite(businessId, item.id, "draft_page"))}>
                        Make a draft page
                    </PillButton>
                )}
                {canGet && (
                    <PillButton size="sm" variant={clientYes ? "primary" : "secondary"} icon={DownloadIcon} busy={busy} onClick={() => void onWrite("The copy is ready to paste, block by block", () => copyToSite(businessId, item.id, "download"))}>
                        Get the copy to paste
                    </PillButton>
                )}
                {c.draftUrl && /^https?:/i.test(c.draftUrl) && (
                    <a href={c.draftUrl} target="_blank" rel="noopener noreferrer" className={cn(TEXT_LINK, "inline-flex items-center gap-1")}>
                        Open the draft
                        <ExternalLinkIcon aria-hidden size={12} />
                        <span className="sr-only">, opens in a new tab</span>
                    </a>
                )}
                {download}
                {c.docId && (
                    <button type="button" onClick={() => onOpenDoc(c.docId!)} className={TEXT_LINK}>
                        Open the doc
                    </button>
                )}
            </div>
        </WorkBlock>
    );
}

/* ------------------------------------------------------------------------- *
 * AI readers (H38), here and on the Site screen
 * ------------------------------------------------------------------------- */

function readerFacts(r: ReaderCheck): string {
    const robots = r.robotsAllows == null ? "robots.txt not read" : r.robotsAllows ? "robots.txt allows it" : "robots.txt blocks it";
    const got = r.gotIn == null ? "not fetched" : r.gotIn ? "got in" : "turned away";
    const words = r.gotIn == null || r.wordsVisible == null ? null : r.wordsVisible ? "words visible without scripts" : "no words without scripts";
    return [robots, got, words].filter(Boolean).join(" · ");
}

/** One line per AI reader: robots.txt, got in, words without scripts, checked at. */
export function ReaderLines({ checks }: { checks: readonly ReaderCheck[] }) {
    return (
        <div>
            {checks.map((r) => {
                const tone: Tone = r.checkedAt == null ? "gray" : readerPasses(r) ? "green" : "blue";
                return (
                    <ListRow
                        key={r.reader}
                        lead="dot"
                        icon={<Dot tone={tone} />}
                        title={
                            <>
                                {READER_NAME[r.reader]} <span className="font-normal text-gray-500">· {r.agent}</span>
                            </>
                        }
                        sub={readerFacts(r)}
                        meta={r.checkedAt ? `Checked ${day(r.checkedAt)}` : "Not tested"}
                        muted={r.checkedAt == null}
                        stackMeta
                    />
                );
            })}
        </div>
    );
}

function ReadersBlock({ item, businessId, busy, onWrite }: { item: DeliveryItem; businessId: string; busy: boolean; onWrite: WorkWrite }) {
    // Before the first test (null or empty), each reader reads "Not tested".
    const tested = Boolean(item.readerChecks?.length);
    const checks = tested ? item.readerChecks! : UNTESTED_READERS;
    const pass = checks.filter(readerPasses).length;
    return (
        <WorkBlock label="AI reader test" fact={tested ? `${pass} of ${checks.length} get in` : "Not tested"}>
            <ReaderLines checks={checks} />
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span className={cn(TYPE.tm, "text-gray-500")}>{tested && pass === checks.length ? "All 3 get in, which closes this item." : "This item closes when all 3 get in."}</span>
                <PillButton size="sm" busy={busy} onClick={() => void onWrite("AI readers tested", () => testAiReaders(businessId))}>
                    {tested ? "Test again" : "Test the AI readers"}
                </PillButton>
            </div>
        </WorkBlock>
    );
}

/* ------------------------------------------------------------------------- *
 * The item's work, in one place
 * ------------------------------------------------------------------------- */

export interface ItemWorkProps {
    item: DeliveryItem;
    /** The item's deliverable: its code keys the client's yes (H20), its doc may already read approved, and a posted Live since reply shows on it. */
    deliverable: Deliverable;
    businessId: string;
    businessName: string;
    busy: boolean;
    onWrite: WorkWrite;
    onOpenDoc: (docId: string) => void;
    onGoTo: (view: string, search?: string) => void;
}

export function ItemWork({ item, deliverable, businessId, businessName, busy, onWrite, onOpenDoc, onGoTo }: ItemWorkProps) {
    if (!DELIVERY_WORK_ON) return null;
    const drafts = item.drafts ?? [];
    const readers = isReadersItem(item);
    const any = drafts.length > 0 || item.pasteList || item.liveSinceDraft || item.copyToSite || readers || item.proofFile || item.impact;
    if (!any) return null;
    return (
        <div className="space-y-2">
            {item.impact && <p className={cn(TYPE.tc, "m-0 text-gray-700")}>{item.impact}</p>}
            {drafts.length > 0 && (
                <WorkBlock label="Drafts" fact={plural(drafts.length, "draft")}>
                    <ul className="m-0 list-plain p-0">
                        {drafts.map((dr) => (
                            <DraftRow key={dr.id} draft={dr} item={item} onOpenDoc={onOpenDoc} onGoTo={onGoTo} />
                        ))}
                    </ul>
                </WorkBlock>
            )}
            {item.pasteList && <PasteListBlock item={item} code={deliverable.code} businessId={businessId} businessName={businessName} busy={busy} onWrite={onWrite} />}
            {item.copyToSite && <CopyToSiteBlock item={item} deliverable={deliverable} businessId={businessId} businessName={businessName} busy={busy} onWrite={onWrite} onOpenDoc={onOpenDoc} />}
            {readers && <ReadersBlock item={item} businessId={businessId} busy={busy} onWrite={onWrite} />}
            {item.liveSinceDraft && <LiveSinceBlock item={item} deliverable={deliverable} businessId={businessId} busy={busy} onWrite={onWrite} onOpenDoc={onOpenDoc} />}
            {item.proofFile && (
                <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-baseline gap-x-2 text-gray-600")}>
                    <span className="min-w-0 break-words">
                        Proof file: {item.proofFile.name} <span className="text-gray-500">· {item.proofFile.addedBy}, {day(item.proofFile.addedAt)}</span>
                    </span>
                    <a href={item.proofFile.url} target="_blank" rel="noopener noreferrer" className={TEXT_LINK}>
                        Open the file
                    </a>
                </p>
            )}
        </div>
    );
}
